import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, CircleAlert, Info, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { DefinitionGrid } from '@/components/common/DefinitionGrid';
import UploadStatusBadge from './UploadStatusBadge';
import UploadStatusIcon from './UploadStatusIcon';
import { cn } from '@/lib/utils';
import { formatFileSize } from '@utils/dateUtils';
import type { UploadFile } from '@hooks/useDocumentUpload';

interface UploadProgressDetailsProps {
  uploadFile: UploadFile;
}

const UploadProgressDetails: React.FC<UploadProgressDetailsProps> = ({ uploadFile }) => {
  const { t } = useTranslation();
  const { metadata } = uploadFile;

  return (
    <div className="px-3 pb-3 pl-10">
      <Card className="shadow-none">
        <CardContent className="space-y-3 p-3">
          <p className="revy-cjk text-xs font-medium leading-[1.5]">
            {t('upload.progressTracker.fileDetails')}
          </p>

          <DefinitionGrid
            className="text-xs"
            items={[
              {
                label: t('upload.progressTracker.fileSize'),
                value: formatFileSize(uploadFile.file.size),
              },
              {
                label: t('upload.progressTracker.fileType'),
                value: uploadFile.file.type || 'Unknown',
              },
              {
                label: t('upload.progressTracker.progress'),
                value: `${Math.round(uploadFile.progress)}%`,
              },
            ]}
          />

          {metadata && (
            <div className="space-y-2">
              <p className="revy-cjk text-xs font-medium leading-[1.5]">
                {t('upload.progressTracker.metadata')}
              </p>
              <DefinitionGrid
                className="text-xs"
                items={[
                  { label: t('upload.progressTracker.category'), value: metadata.category },
                  { label: t('upload.progressTracker.description'), value: metadata.description },
                ]}
              />
            </div>
          )}

          {uploadFile.error && (
            <Alert variant="destructive">
              <CircleAlert className="size-4" strokeWidth={1.9} />
              <AlertDescription className="revy-cjk leading-[1.5]">
                {uploadFile.error}
              </AlertDescription>
            </Alert>
          )}

          {uploadFile.status === 'processing' && uploadFile.statusMessage && (
            <Alert variant="info">
              <Info className="size-4" strokeWidth={1.9} />
              <AlertDescription className="revy-cjk leading-[1.5]">
                {uploadFile.statusMessage}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

interface UploadProgressItemProps {
  uploadFile: UploadFile;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onRetry: () => void;
}

const UploadProgressItem: React.FC<UploadProgressItemProps> = ({
  uploadFile,
  isExpanded,
  onToggleExpanded,
  onRetry,
}) => {
  const { t } = useTranslation();
  const hasError = uploadFile.status === 'error';
  const isActive = ['uploading', 'processing'].includes(uploadFile.status);

  return (
    <li
      className={cn(
        'rounded-md border',
        hasError
          ? 'border-destructive/40 bg-destructive/5'
          : 'border-border hover:bg-accent/50',
      )}
    >
      <div className="flex items-start gap-3 px-3 py-2">
        <UploadStatusIcon status={uploadFile.status} className="mt-1" />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span title={uploadFile.file.name} className="revy-cjk min-w-0 truncate text-sm leading-[1.5]">
              {uploadFile.file.name}
            </span>
            <UploadStatusBadge status={uploadFile.status} progress={uploadFile.progress} />
          </div>

          <span className="revy-figure block text-xs leading-[1.5] text-muted-foreground">
            {formatFileSize(uploadFile.file.size)}
          </span>

          {isActive && (
            <>
              <Progress value={uploadFile.progress} className="mt-1 h-1" />
              {uploadFile.statusMessage && (
                <span className="revy-cjk mt-1 block text-xs leading-[1.5] text-muted-foreground">
                  {uploadFile.statusMessage}
                </span>
              )}
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {hasError && (
            <TooltipIconButton
              label={t('common.retry')}
              icon={<RefreshCw className="size-4" strokeWidth={2} />}
              onClick={onRetry}
            />
          )}
          <TooltipIconButton
            label={isExpanded ? t('common.collapse') : t('common.expand')}
            icon={
              isExpanded ? (
                <ChevronUp className="size-4" strokeWidth={2} />
              ) : (
                <ChevronDown className="size-4" strokeWidth={2} />
              )
            }
            aria-expanded={isExpanded}
            onClick={onToggleExpanded}
          />
        </div>
      </div>

      {isExpanded && <UploadProgressDetails uploadFile={uploadFile} />}
    </li>
  );
};

export default UploadProgressItem;
